import { Iam as IamSdk2019 } from "@smash-sdk/iam/10-2019";
import bluebird from "bluebird";
import { getDecodedToken } from "../../../shared/helpers/helpers";
import { roles } from "../../roles/accessManagement/config/roles";
import { UserRole } from "../../roles/accessManagement/roles";
import { ApiKeyDisplay } from "../models/ApiKey.interface";
import { DeleteKeyInput } from "../models/DeleteKeyInput.interface";
import { GenerateKeyInput } from "../models/GenerateKey.interface";
import { GenerateApiKeyRoleInput } from "../models/GenerateTeamRoleInput.interface";
import { RenewKeyInput } from "../models/RenewKeyInput.interface";


const generateApiKeyRole = async (generateApiKeyRoleInput: GenerateApiKeyRoleInput): Promise<void> => {
    try {
        const { userId } = generateApiKeyRoleInput;
        const { account } = getDecodedToken();
        const role = new UserRole(roles.fullBasicAccess, { account, userId });
        const policies = role.policies;
        const policiesUnique = [];
        const policiesToCreate = new Map(); // Map to store unique policy names
        for (const policy of policies) {
            if (!policiesToCreate.has(policy.name)) {
                policiesToCreate.set(policy.name, true);
                policiesUnique.push(policy);
            }
        }
        const iam2019 = new IamSdk2019({});
        await bluebird.map(policiesUnique, async (policy: any) => await iam2019.createPolicy(policy), { concurrency: 10 });
        await iam2019.createRole(role);
        await bluebird.map(role.policies, async (policy) => await iam2019.attachRolePolicy({ roleId: role.name, policyId: policy.name }));
        await iam2019.attachUserRole({ userId, roleId: role.name });
        return;
    } catch (error) {
        throw error;
    }
}

const generateKey = async (userInput: GenerateKeyInput): Promise<{ apiKey: string | null, expiration: string | null }> => {
    try {
        const iam2019 = new IamSdk2019({});
        const createdUser = await iam2019.createUser({ ...userInput });
        if (createdUser.user) {
            const { user } = createdUser;
            const { id } = user;

            await generateApiKeyRole({ userId: id });

            const { tokenDuration } = userInput;
            const createdUserToken = await iam2019.createUserToken({ userId: id, tokenDuration });
            if (createdUserToken.user) {
                const { user } = createdUserToken;
                return { apiKey: user.token.token, expiration: user.token.expiration }
            }
        }
        return { apiKey: null, expiration: null }
    } catch (error) {
        throw error
    }
}

const listApiKeys = async (): Promise<{ apiKeys: ApiKeyDisplay[] }> => {
    try {
        const iam2019 = new IamSdk2019({})
        const response = await iam2019.listUsers();
        if (response.users) {
            const { users } = response;
            return { apiKeys: users }
        }
        return { apiKeys: [] }
    } catch (error) {
        throw error
    }
}

const getApiKeyTeam = async (apiKey: string) => {
    try {
        const iam2019 = new IamSdk2019({})
        const response = await iam2019.listUserTeams({ userId: apiKey });
        if (response?.teams?.length) {
            const { teams } = response;
            return { team: teams.pop() }
        }
        return { team: undefined }
    } catch (error) {
        throw error
    }
}

const deleteKey = async (input: DeleteKeyInput): Promise<{}> => {
    try {
        const iam2019 = new IamSdk2019({})
        const { teams } = await iam2019.listUserTeams(input);
        const { roles } = await iam2019.listUserRoles(input);
        const promises: Promise<Object>[] = [];
        teams.map((team: any) => promises.push(iam2019.detachTeamUser({ ...input, teamId: team.id }))); //FIXME: type team
        roles.map((role: any) => promises.push(iam2019.detachUserRole({ ...input, roleId: role.name }))); //FIXME type role
        await Promise.all(promises);
        return await iam2019.deleteUser(input);
    } catch (error) {
        throw error
    }
}

const renewKey = async (userInput: RenewKeyInput): Promise<{ apiKey: string | null, expiration: string | null }> => {
    try {
        const { id, tokenDuration } = userInput;
        const iam2019 = new IamSdk2019({})
        const createdUserToken = await iam2019.createUserToken({ userId: id, tokenDuration });
        if (createdUserToken.user) {
            const { user } = createdUserToken;
            return { apiKey: user.token.token, expiration: user.token.expiration }
        }
        return { apiKey: null, expiration: null }
    } catch (error) {
        throw error
    }
}

const apiKeyService = {
    generateKey,
    listApiKeys,
    deleteKey,
    renewKey,
    getApiKeyTeam
}

export default apiKeyService;